import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Film, Sparkles, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import MovieCard from "../components/MovieCard";

export default function GenrePage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setData(null); setNotFound(false);
    api.get(`/genres/${slug}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setNotFound(e.response?.status === 404); });
    return () => { cancelled = true; };
  }, [slug]);

  // SEO: set document title + meta description for this shelf
  useEffect(() => {
    if (!data) return;
    const g = data.genre;
    document.title = `${g.name} films · Christoreel`;
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement("meta"); m.setAttribute("name", "description"); document.head.appendChild(m); return m;
    })();
    meta.setAttribute("content", g.tagline || `The best African Christian ${g.name.toLowerCase()} films, streaming on Christoreel.`);
    return () => { document.title = "Christoreel"; };
  }, [data]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="genre-not-found">
        <Film className="h-10 w-10 mx-auto text-white/20 mb-5" />
        <h1 className="font-display text-3xl font-black mb-3">Genre not found</h1>
        <p className="text-white/60 mb-8">We don't have a "{slug}" shelf. Browse the full library instead.</p>
        <Link to="/browse" className="ember-btn"><ArrowLeft className="h-4 w-4" /> Back to browse</Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-[1300px] mx-auto px-6 lg:px-12 py-16">
        <div className="h-7 w-32 rounded bg-white/[0.06] animate-pulse mb-6" />
        <div className="h-12 w-2/3 max-w-xl rounded bg-white/[0.06] animate-pulse mb-4" />
        <div className="h-5 w-1/2 max-w-md rounded bg-white/[0.04] animate-pulse mb-12" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
          {[0,1,2,3,4,5,6,7,8,9].map((i) => (
            <div key={i} className="aspect-[2/3] rounded-xl bg-white/[0.04] animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const { genre, movies, series, counts } = data;
  const totalCount = counts.movies + counts.series;

  return (
    <div className="max-w-[1300px] mx-auto px-6 lg:px-12 py-16" data-testid="genre-page">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-white/40 mb-6" data-testid="genre-breadcrumb">
        <Link to="/browse" className="hover:text-white transition">Browse</Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <span className="text-white/70">{genre.name}</span>
      </nav>

      {/* Hero */}
      <div className="flex items-center gap-3 mb-4">
        <Sparkles className="h-4 w-4 text-[#E65F2B]" />
        <p className="text-eyebrow text-[#E65F2B]" data-testid="genre-eyebrow">Christoreel · {genre.name}</p>
      </div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] mb-5" data-testid="genre-title">
        {genre.tagline || `${genre.name} films, made in faith.`}
      </h1>
      {genre.intro ? (
        <p className="text-base sm:text-lg text-white/60 max-w-3xl leading-relaxed whitespace-pre-line" data-testid="genre-intro">
          {genre.intro}
        </p>
      ) : (
        <p className="text-base sm:text-lg text-white/60 max-w-3xl leading-relaxed" data-testid="genre-intro-default">
          Curated {genre.name.toLowerCase()} titles from African filmmakers across the continent. Stream the latest, follow your favorite channels, and let the screen serve the Word.
        </p>
      )}

      {/* Stats strip */}
      <div className="flex items-center gap-6 mt-8 text-sm text-white/40" data-testid="genre-stats">
        <span><span className="text-white font-bold">{counts.movies}</span> film{counts.movies === 1 ? "" : "s"}</span>
        {counts.series > 0 && <span><span className="text-white font-bold">{counts.series}</span> series</span>}
      </div>

      {/* Empty state */}
      {totalCount === 0 ? (
        <div className="card-surface p-16 text-center mt-16" data-testid="genre-empty">
          <Film className="h-10 w-10 mx-auto text-white/20 mb-5" />
          <h2 className="font-display text-2xl font-bold mb-2">No {genre.name.toLowerCase()} titles yet</h2>
          <p className="text-white/50 max-w-md mx-auto mb-8">
            We're still curating this shelf. Check back soon, or browse the full Christoreel library in the meantime.
          </p>
          <Link to="/browse" className="ember-btn">Browse all films</Link>
        </div>
      ) : (
        <>
          {/* Movies grid */}
          {movies.length > 0 && (
            <section className="mt-16" data-testid="genre-movies-section">
              <h2 className="font-display text-2xl font-black mb-6">Films</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
                {movies.map((m) => <MovieCard key={m.id} movie={m} />)}
              </div>
            </section>
          )}

          {/* Series list */}
          {series.length > 0 && (
            <section className="mt-16" data-testid="genre-series-section">
              <h2 className="font-display text-2xl font-black mb-6">Series</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {series.map((s) => (
                  <Link key={s.id} to={`/series/${s.id}`} className="card-surface p-5 hover:border-white/20 transition group" data-testid={`genre-series-${s.id}`}>
                    <p className="text-eyebrow text-[#E65F2B] mb-2">{s.episode_count || 0} episode{s.episode_count === 1 ? "" : "s"}</p>
                    <h3 className="font-display font-bold text-lg group-hover:text-[#E65F2B] transition">{s.title}</h3>
                    {s.description && <p className="text-sm text-white/60 mt-2 line-clamp-2">{s.description}</p>}
                  </Link>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
